import { AlertTriangle, CheckCircle2, FileQuestion, TriangleAlert } from "lucide-react"

import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"

import type { NoMapeado } from "@/routes/statements/api"
import { t } from "@/lib/i18n"

/**
 * El cuadre de un estado contable, arriba y no al pie.
 *
 * Es lo primero que mira un contador y lo que decide si lo demas se puede leer.
 * Cuando cuadra la senal es sobria; cuando no, ocupa espacio y muestra la
 * DIFERENCIA, que es el unico numero que importa en ese momento.
 */
export function StatementCheck({
  cuadra,
  diferencia,
  etiqueta,
  detalle,
}: {
  cuadra: boolean
  diferencia: number
  etiqueta: string
  detalle?: string
}) {
  if (cuadra) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-success/25 bg-success-soft px-2.5 py-1.5 text-xs">
        <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
        <span className="font-medium">{etiqueta}</span>
        {detalle ? <span className="text-muted-foreground">{detalle}</span> : null}
      </div>
    )
  }

  return (
    <div className="rounded-md border border-danger/40 bg-danger-soft px-2.5 py-2">
      <div className="flex items-center gap-2">
        <TriangleAlert className="h-4 w-4 text-danger" aria-hidden="true" />
        <span className="text-xs font-semibold">{etiqueta}</span>
      </div>
      <p className="mt-1 num-strong text-lg text-danger">{formatAmount(diferencia)}</p>
      <p className="mt-0.5 text-2xs text-muted-foreground">
        {t("Un estado contable que no cuadra no es una aproximacion: es un error, y lo que venga despues no se puede leer hasta arreglarlo.")}</p>
    </div>
  )
}

/**
 * El aviso de lo que no se pudo mapear al plan de grupo.
 *
 * Va aparte del cuadre porque es un problema DISTINTO y peor de ver: un asiento
 * entero sin mapear netea a cero, el estado cuadra, y sus importes no estan en
 * ninguna linea. No falta el cuadre — falta el contenido.
 */
export function UnmappedWarning({ noMapeado }: { noMapeado?: NoMapeado }) {
  if (!noMapeado?.hay) return null

  const neteaACero = Math.abs(noMapeado.neto) < 0.005
  return (
    <div
      className={cn(
        "flex flex-wrap items-start gap-2 rounded-md border px-2.5 py-2 text-xs",
        "border-warning/40 bg-warning-soft",
      )}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
      <div className="space-y-0.5">
        <p className="font-medium">
          {t("Hay")}{" "}{formatAmount(noMapeado.bruto)}{" "}{t("en lineas sin cuenta de grupo.")}</p>
        <p className="text-2xs text-muted-foreground">
          {neteaACero
            ? "Netean a cero, asi que el estado cuadra igual — pero sus importes no aparecen en ninguna linea. Falta mapearlas en account_map."
            : "Su neto se descuenta del cuadre para que este no mienta. Falta mapearlas en account_map."}
        </p>
      </div>
    </div>
  )
}

/**
 * Un estado en ceros porque el libro esta vacio.
 *
 * Un cero en un estado contable ES UNA AFIRMACION: dice que no hubo actividad.
 * Cuando lo que pasa es que nadie asento nada todavia, mostrar ceros miente, y
 * miente en el formato que mas confianza inspira.
 */
export function EmptyBook({ asientos }: { asientos: number }) {
  if (asientos > 0) return null
  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border bg-surface-sunken px-2.5 py-2 text-xs">
      <FileQuestion className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="space-y-0.5">
        <p className="font-medium">{t("El libro no tiene asientos en este rango.")}</p>
        <p className="text-2xs text-muted-foreground">
          {t("Los ceros de abajo no dicen que no hubo actividad: dicen que no hay nada asentado. Los comprobantes se asientan al aprobarlos en Contabilidad &gt; Revision.")}</p>
      </div>
    </div>
  )
}
